import { Image as RNEImage, makeStyles } from '@rneui/themed'
import { useNewsfeedMessageImageStyles } from 'chat/shared/util'
import { Image } from 'expo-image'
import { useState } from 'react'
import { ActivityIndicator, View, useWindowDimensions } from 'react-native'
import { NonSelectableText } from 'shared/components/chat/non_selectable_text'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import {
  getDarkShadeColor,
  getLightShadeColor,
  stripInvalidCodepoints,
} from 'shared/utils'
import { useLinkPreview } from '../queries/link_preview'
import { THUMBNAIL_IMAGE_WIDTH } from './thumbnail_image'

export const LinkPreviewComponent = ({
  url,
  onLongPress,
  isSender,
  onLinkPress,
}: {
  url: string
  onLongPress: () => void
  isSender: boolean
  onLinkPress: (url: string) => void
}) => {
  const linkPreviewQuery = useLinkPreview(url)

  const styles = useStyles({ isSender })
  if (!url) {
    return null
  }
  const data = linkPreviewQuery.data

  if (linkPreviewQuery.isLoading && !data) {
    return (
      <View style={styles.linkPreviewActivityContainer}>
        <ActivityIndicator />
      </View>
    )
  }

  if (!data) {
    return null
  }

  return (
    <PressableOpacity
      onPress={() => onLinkPress(url)}
      onLongPress={onLongPress}
      style={styles.linkPreviewContainer}
    >
      {data.image ? <LinkPreviewImage url={data?.image} /> : null}
      <View style={styles.linkDescriptionContainer}>
        {data.title ? (
          <NonSelectableText
            numberOfLines={2}
            style={styles.title}
            textBreakStrategy="simple"
          >
            {stripInvalidCodepoints(data.title)}
          </NonSelectableText>
        ) : null}
        {data.description ? (
          <NonSelectableText
            numberOfLines={2}
            style={styles.description}
            textBreakStrategy="simple"
          >
            {stripInvalidCodepoints(data.description)}
          </NonSelectableText>
        ) : null}
        {data.domain ? (
          <NonSelectableText style={styles.domain} textBreakStrategy="simple">
            {stripInvalidCodepoints(data.domain)}
          </NonSelectableText>
        ) : null}
      </View>
    </PressableOpacity>
  )
}

export const NewsfeedLinkPreviewComponent = ({
  url,
  onLongPress,
  onLinkPress,
}: {
  url: string
  onLongPress: () => void
  onLinkPress: (url: string) => void
}) => {
  const linkPreviewQuery = useLinkPreview(url)
  const dimensions = useWindowDimensions()
  const styles = useStyles()
  const newsfeedImageStyles = useNewsfeedMessageImageStyles({
    width: dimensions.width,
  })
  if (!url) {
    return null
  }
  const data = linkPreviewQuery.data

  if (linkPreviewQuery.isLoading) {
    return (
      <View
        style={[
          newsfeedImageStyles,
          styles.newsfeedLinkPreviewActivityContainer,
        ]}
      >
        <ActivityIndicator />
      </View>
    )
  }

  if (!data) {
    return null
  }
  return (
    <PressableOpacity
      onPress={() => onLinkPress(url)}
      onLongPress={onLongPress}
      style={styles.newsfeedLinkPreviewContainer}
    >
      {data.image ? <NewsfeedLinkPreviewImage url={data?.image} /> : null}
      <View style={styles.newsfeedLinkDescriptionContainer}>
        {data.title ? (
          <NonSelectableText
            numberOfLines={2}
            style={styles.title}
            textBreakStrategy="simple"
          >
            {stripInvalidCodepoints(data.title)}
          </NonSelectableText>
        ) : null}
        {data.description ? (
          <NonSelectableText
            numberOfLines={2}
            style={styles.description}
            textBreakStrategy="simple"
          >
            {stripInvalidCodepoints(data.description)}
          </NonSelectableText>
        ) : null}
        {data.domain ? (
          <NonSelectableText style={styles.domain} textBreakStrategy="simple">
            {stripInvalidCodepoints(data.domain)}
          </NonSelectableText>
        ) : null}
      </View>
    </PressableOpacity>
  )
}

const LinkPreviewImage = ({ url }: { url: string }) => {
  const [loading, setLoading] = useState(true)
  const [aspectRatio, setAspectRatio] = useState<number>(1)
  const styles = useStyles()

  RNEImage.getSize(
    url,
    (width, height) => {
      setAspectRatio(width / height)
      setLoading(false)
    },
    error => {
      setLoading(false)
    }
  )

  if (loading) {
    return (
      <View style={styles.linkPreviewImage}>
        <ActivityIndicator />
      </View>
    )
  }
  return (
    <Image
      source={{ uri: url }}
      style={{
        width: THUMBNAIL_IMAGE_WIDTH,
        aspectRatio,
      }}
      contentFit="cover"
    />
  )
}

const NewsfeedLinkPreviewImage = ({ url }: { url: string }) => {
  const [loading, setLoading] = useState(true)
  const [aspectRatio, setAspectRatio] = useState<number>(1)
  const styles = useStyles()
  const dimensions = useWindowDimensions()
  RNEImage.getSize(url, (width, height) => {
    setAspectRatio(width / height)
    setLoading(false)
  })
  const { width, maxWidth } = useNewsfeedMessageImageStyles({
    width: dimensions.width,
  })
  if (loading) {
    return (
      <View
        style={[
          {
            width,
            height: width / 2,
            maxWidth,
          },
          styles.newsfeedLinkPreviewActivityContainer,
        ]}
      >
        <ActivityIndicator />
      </View>
    )
  }
  return (
    <Image
      source={{ uri: url }}
      style={{
        width: width,
        maxWidth,
        aspectRatio,
      }}
      contentFit="cover"
    />
  )
}

const useStyles = makeStyles((theme, { isSender }: { isSender: boolean }) => ({
  linkPreviewActivityContainer: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
    marginTop: 5,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 5,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor:
      theme.mode === 'dark' || isSender
        ? getDarkShadeColor()
        : getLightShadeColor(),
  },
  linkPreviewContainer: {
    marginHorizontal: 5,
    marginTop: 5,
    borderRadius: 8,
    overflow: 'hidden',
    width: THUMBNAIL_IMAGE_WIDTH,
    backgroundColor:
      theme.mode === 'dark' || isSender
        ? getDarkShadeColor()
        : getLightShadeColor(),
  },
  linkPreviewImage: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkDescriptionContainer: {
    backgroundColor:
      theme.mode === 'dark' || isSender
        ? getDarkShadeColor()
        : getLightShadeColor(),
    padding: 5,
  },
  newsfeedLinkPreviewActivityContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor() : getLightShadeColor(),
  },
  newsfeedLinkPreviewContainer: {
    overflow: 'hidden',
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor() : getLightShadeColor(1),
  },
  newsfeedLinkDescriptionContainer: {
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor() : getDarkShadeColor(0.1),
    padding: 5,
  },
  title: { color: isSender ? 'white' : theme.colors.black, fontSize: 16 },
  description: {
    color: isSender ? 'white' : theme.colors.black,
    opacity: 0.7,
    fontSize: 14,
  },
  domain: {
    color: isSender ? 'white' : theme.colors.black,
    opacity: 0.5,
    fontSize: 12,
  },
}))
